import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import { AgeHistoryPreviewDocument, type AgeHistoryRow, AgeNotesByIdsDocument } from "#app/lib/data";

/** Pull the embedded note id off an age-update changeset, if one was attached. */
function ageNoteId(update: AgeHistoryRow): string | undefined {
  return update.changeset?.age?.after?.noteId ?? undefined;
}

/**
 * Hover-preview data for a kanban card's age button. Owns its own slim
 * `AgeHistoryPreview` query, filtered server-side to `subject: "age"`
 * activities only (not the kanban board read, not the activity view's
 * ActivityViewData) and ordered newest-first by the server. Mounts only
 * while one card's popover is open, so it's a single active query.
 *
 * Age updates only carry a `noteId` when a comment was attached; their text
 * isn't on the activity, so a second query resolves the bodies for exactly the
 * visible updates' note ids and returns a `noteId → text` map. That query is
 * skipped entirely when no visible update has a note.
 */
export function useAgeHistory(issueId: string): {
  updates: AgeHistoryRow[];
  notesById: Map<string, string>;
  loading: boolean;
} {
  // Preview shows only the five most recent age updates.
  const variables = useMemo(() => ({ issueId, limit: 5 }), [issueId]);
  const { data, loading } = useQuery(AgeHistoryPreviewDocument, {
    variables,
    fetchPolicy: "cache-and-network",
  });

  const updates = useMemo(
    () => data?.activities?.edges?.map((e) => e.node) ?? [],
    [data],
  );

  const noteIds = useMemo(
    () => Array.from(new Set(updates.map(ageNoteId).filter(Boolean) as string[])),
    [updates],
  );

  const noteVariables = useMemo(() => ({ ids: noteIds }), [noteIds]);
  const { data: notesData } = useQuery(AgeNotesByIdsDocument, {
    variables: noteVariables,
    skip: noteIds.length === 0,
    fetchPolicy: "cache-and-network",
  });

  const notesById = useMemo(() => {
    const map = new Map<string, string>();
    notesData?.notes?.edges?.forEach((e) => map.set(e.node.noteId, e.node.note));
    return map;
  }, [notesData]);

  return { updates, notesById, loading };
}
